import { Fragment, FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { CONTROLS_MOVE_MOUSE, CONTROLS_SHOW_TIME } from '../controlsPlugin.constants';
import {
  setUserInteraction,
} from '../store/controlsPlugin.actions';
import { useKeyDown } from '../../../common/hooks/useKeyDown';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { isDesktopMode, isEmpty } from '../../../common/utils/utils';
import { FullVideoControls } from './fullVideoControls/fullVideoControls';
import { PartialVideoControls } from './partialVideoControls/partialVideoControls';
import { CloseButton } from '../closeButton/closeButton';
import { brandingDataSelector } from '../../../common/store/selectors/brandingData.selector';
import { playerSettingsSelector } from '../../../common/store/selectors/playerSettings.selector';
import { setPendingAdStatus } from '../../../core/store/monetization/monetizationSlice';
import { resumeVideo, pauseVideo, setShowControls } from '../../../core/store/playerData/playerDataSlice';
import { updateAnchorDisabledByUser } from '../../../core/store/anchorOptionsSlice/anchorOptionsSlice';
import { playNextVideo } from '../../../core/store/mediaData/mediaDataSlice';
import { StoriesTitle } from '../storiesTitle/storiesTitle';
import { recirculationVideoDataSelector } from '../../../common/store/selectors/recirculationVideoData.selector';

interface VideoControlsComponentVariablesProps {
  hideAllControls: boolean;
  fullscreenContainerRef: any;
}

export const VideoControls: FunctionComponent<VideoControlsComponentVariablesProps> = props => {
  const { hideAllControls, fullscreenContainerRef } = props;

  const dispatch = useDispatch();
  const playbackMethod = useSelector(playerDataSelector.playbackMethod);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const playerMode = useSelector(playerDataSelector.playerMode);
  const isVastAd = useSelector(adsDataSelector.isVastAd);
  const adStatus = useSelector(adsDataSelector.adStatus);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const showControls = useSelector(playerDataSelector.showControls);
  const controlsDisplay = useSelector(brandingDataSelector.controlsDisplay);
  const isFullscreenOn = useSelector(playerSettingsSelector.isFullscreenOn);
  const fullscreenContainer = fullscreenContainerRef ? fullscreenContainerRef.current : null;
  const currentSceneTitle = useSelector(recirculationVideoDataSelector.currentSceneTitle);
  const numberOfScenes = useSelector(recirculationVideoDataSelector.numberOfScenes);

  const mouseMoveTimeout = useRef(null);
  const [isMouseMove, setIsMouseMove] = useState(false);

  const desktopMode = isDesktopMode(playerMode);
  const showAdControls = isVastAd && adBreak;
  const clickToPlayMode = videoTagStatus === 'idle' && playbackMethod === 'none' && !showAdControls;
  const pausedState = videoTagStatus === 'paused' || adStatus === 'paused';
  const viewDisplay = controlsDisplay === 'viewDisplay' && !isFullscreenOn;
  const partialVideoControls = (playerSize === 'extraSmall' || playerSize === 'small' || (playerSize === 'medium' && showAdControls) || viewDisplay) && !clickToPlayMode;
  const isRecirculationVideo = numberOfScenes !== 0;
  const showRecirculationTitle = isRecirculationVideo && !isEmpty(currentSceneTitle);

  const attemptToggleControls = useCallback((nextState: boolean): void => {
    const finalNextState = hideAllControls ? false : nextState;
    dispatch(setShowControls(finalNextState));
  }, [hideAllControls, dispatch]);

  const onMouseMove = useCallback((): void => {
    if (desktopMode) {
      clearTimeout(mouseMoveTimeout.current);

      if (!showControls) {
        attemptToggleControls(true);
        setIsMouseMove(true);
      }

      mouseMoveTimeout.current = setTimeout(() => {
        setIsMouseMove(false);
      }, CONTROLS_MOVE_MOUSE);
    }
  }, [desktopMode, showControls, attemptToggleControls]);

  const onScreenClicked = useCallback((): void => {
    if (playerSize === 'medium' || playerSize === 'small' || playerSize === 'extraSmall') {
      attemptToggleControls(true);
      return;
    }
    if (!showAdControls) {
      switch (videoTagStatus) { // DESKTOP SCREEN CLICKED
        case 'playing': {
          dispatch(pauseVideo());
          attemptToggleControls(true);
          dispatch(setUserInteraction('pause'));
          break;
        }
        case 'paused':
        case 'seeking': {
          dispatch(resumeVideo());
          dispatch(updateAnchorDisabledByUser(false));
          attemptToggleControls(true);
          dispatch(setUserInteraction('play'));
          break;
        }
        default: {
          break;
        }
      }
    } else {
      switch (adStatus) { // DESKTOP SCREEN CLICKED ON AD
        case 'playing': {
          dispatch(setPendingAdStatus('paused'));
          attemptToggleControls(true);
          break;
        }
        case 'paused': {
          dispatch(setPendingAdStatus('playing'));
          dispatch(updateAnchorDisabledByUser(false));
          attemptToggleControls(true);
          break;
        }
        default: {
          break;
        }
      }
    }
  }, [adStatus, dispatch, playerSize, showAdControls, attemptToggleControls, videoTagStatus]);

  useEffect(() => {
    if (fullscreenContainer) {
      fullscreenContainer.addEventListener('click', onScreenClicked);
      fullscreenContainer.addEventListener('mousemove', onMouseMove);
    }
    return (): void => {
      if (fullscreenContainer) {
        fullscreenContainer.removeEventListener('click', onScreenClicked);
        fullscreenContainer.removeEventListener('mousemove', onMouseMove);
      }
    };
  }, [fullscreenContainer, onMouseMove, onScreenClicked]);

  useEffect(() => {
    if (pausedState) {
      attemptToggleControls(true);
    }
    if (videoTagStatus === 'complete') {
      attemptToggleControls(false);
    }
    if (hideAllControls) {
      dispatch(setShowControls(false));
    }
  }, [videoTagStatus, pausedState, attemptToggleControls, hideAllControls, dispatch]);

  useEffect(() => {
    const timerHideControls = setTimeout(() => {
      if (!isMouseMove && !pausedState && !clickToPlayMode) {
        attemptToggleControls(false);
      }
    }, CONTROLS_SHOW_TIME);
    return (): void => clearTimeout(timerHideControls);
  }, [clickToPlayMode, isMouseMove, pausedState, showAdControls, showControls, attemptToggleControls]);

  useKeyDown((event: any) => {
    if (event.shiftKey && event.key === 'N') {
      dispatch(playNextVideo());
    }
  }, [dispatch]);

  return (
    <Fragment>
      <CloseButton />
      {partialVideoControls
        ? (
          <PartialVideoControls
            clickToPlayMode={clickToPlayMode}
          />
        )
        : (
          <FullVideoControls
            clickToPlayMode={clickToPlayMode}
          />
        )}
      {showRecirculationTitle && <StoriesTitle />}
    </Fragment>

  );
};
